import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, isAuthenticated } from '../../utils/auth';
import { Lock, Mail, Eye, EyeOff, LogIn, ShieldCheck } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) navigate('/admin');
  }, [navigate]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    const result = await login(formData.username, formData.password);
    if (result.success) {
      toast.success('Login successful!');
      setTimeout(() => navigate('/admin'), 700);
    } else {
      toast.error(result.message || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <ToastContainer position="top-right" autoClose={3000} newestOnTop />

      {/* Left panel – brand */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ backgroundColor: '#25343F' }}
      >
        {/* Decorative circles */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-10" style={{ backgroundColor: '#FF9B51' }} />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full opacity-5" style={{ backgroundColor: '#BFC9D1' }} />

        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FF9B51' }}>
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <span className="text-white text-xl font-bold tracking-tight">B&amp;B Co.</span>
          </div>
        </div>

        {/* Tagline */}
        <div className="relative z-10 space-y-4">
          <h1 className="text-4xl font-bold text-white leading-tight">
            Welcome to the<br />
            <span style={{ color: '#FF9B51' }}>Admin Portal</span>
          </h1>
          <p style={{ color: '#BFC9D1' }} className="text-lg leading-relaxed">
            Manage your content, users, and settings from one powerful dashboard.
          </p>
        </div>

        {/* Bottom tagline */}
        <div className="relative z-10">
          <p className="text-xs" style={{ color: '#BFC9D1' }}>
            © {new Date().getFullYear()} B&amp;B Co. — Authorized access only.
          </p>
        </div>
      </div>

      {/* Right panel – form */}
      <div className="flex-1 flex items-center justify-center p-8" style={{ backgroundColor: '#EAEFEF' }}>
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#25343F' }}>
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold" style={{ color: '#25343F' }}>B&amp;B Co.</span>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-brand p-8 border" style={{ borderColor: '#BFC9D1' }}>
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-1" style={{ color: '#25343F' }}>Sign in</h2>
              <p className="text-sm" style={{ color: '#BFC9D1' }}>Enter your username or email and password</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Username */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#25343F' }}>
                  Username / Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4" style={{ color: '#BFC9D1' }} />
                  </div>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    className="block w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none transition-all"
                    style={{
                      borderColor: '#BFC9D1',
                      backgroundColor: '#EAEFEF',
                      color: '#25343F',
                    }}
                    onFocus={e => { e.target.style.borderColor = '#FF9B51'; e.target.style.boxShadow = '0 0 0 3px rgba(255,155,81,0.15)'; }}
                    onBlur={e => { e.target.style.borderColor = '#BFC9D1'; e.target.style.boxShadow = 'none'; }}
                    placeholder="Enter your username"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#25343F' }}>
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4" style={{ color: '#BFC9D1' }} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="block w-full pl-10 pr-12 py-3 rounded-xl border text-sm outline-none transition-all"
                    style={{
                      borderColor: '#BFC9D1',
                      backgroundColor: '#EAEFEF',
                      color: '#25343F',
                    }}
                    onFocus={e => { e.target.style.borderColor = '#FF9B51'; e.target.style.boxShadow = '0 0 0 3px rgba(255,155,81,0.15)'; }}
                    onBlur={e => { e.target.style.borderColor = '#BFC9D1'; e.target.style.boxShadow = 'none'; }}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center transition-opacity hover:opacity-70"
                    style={{ color: '#BFC9D1' }}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-50 shadow-accent"
                style={{ backgroundColor: '#FF9B51', marginTop: '8px' }}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>Signing in…</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t text-center" style={{ borderColor: '#EAEFEF' }}>
              <p className="text-xs" style={{ color: '#BFC9D1' }}>
                <Lock className="inline w-3 h-3 mr-1" />
                Protected — Authorized personnel only
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;